import AdvertisementSvg from 'components/_common/Icons/SVGIcons/AdvertisementSvg';
import AffiliateSvg from 'components/_common/Icons/SVGIcons/AffiliateSvg';
import BlogSvg from 'components/_common/Icons/SVGIcons/BlogSvg';
import CategoriesSvg from 'components/_common/Icons/SVGIcons/CategoriesSvg';
import ContentPagesSvg from 'components/_common/Icons/SVGIcons/ContentPagesSvg';
import CouponSvg from 'components/_common/Icons/SVGIcons/CouponSvg';
import CustomerSvg from 'components/_common/Icons/SVGIcons/CustomerSvg';
import DashboardSvg from 'components/_common/Icons/SVGIcons/DashboardSvg';
import EmployeeSvg from 'components/_common/Icons/SVGIcons/EmployeeSvg';
import FAQSvg from 'components/_common/Icons/SVGIcons/FAQSvg';
import FFLSvg from 'components/_common/Icons/SVGIcons/FFLSvg';
import FileTransferSvg from 'components/_common/Icons/SVGIcons/FileTransferSvg';
import GallerySvg from 'components/_common/Icons/SVGIcons/GallerySvg';
import GunSvg from 'components/_common/Icons/SVGIcons/GunSvg';
import MenufacturereSvg from 'components/_common/Icons/SVGIcons/MenufacturereSvg';
import MessageSvg from 'components/_common/Icons/SVGIcons/MessageSvg';
import OpenEmailSvg from 'components/_common/Icons/SVGIcons/OpenEmailSvg';
import OtherSvg from 'components/_common/Icons/SVGIcons/OtherSvg';
import PaymentCardSvg from 'components/_common/Icons/SVGIcons/PaymentCardSvg';
import ProductSvg from 'components/_common/Icons/SVGIcons/ProductSVG';
import ReturnBoxSvg from 'components/_common/Icons/SVGIcons/ReturnBoxSvg';
import ReviewSvg from 'components/_common/Icons/SVGIcons/ReviewSvg';
import SearchTermsSvg from 'components/_common/Icons/SVGIcons/SearchTermsSvg';
import SettingSvg from 'components/_common/Icons/SVGIcons/SettingSvg';
import ShoppingBagSvg from 'components/_common/Icons/SVGIcons/ShoppingBagSvg';
import SMSTemplateSvg from 'components/_common/Icons/SVGIcons/SMSTemplateSvg';
import SubscriptionSvg from 'components/_common/Icons/SVGIcons/SubscriptionSvg';
import URLRewriteSvg from 'components/_common/Icons/SVGIcons/URLRewriteSvg';
import { placeParamsToURL } from 'helpers/CommonHelper';
import { permissionSlugs } from 'helpers/PermissionHelper';
import { getLocalStorage, removeLocalStorage, storeLocalStorage } from 'helpers/StorageHelper';
import { t } from 'i18next';
import {
  adminBlogRoutes,
  adminAdvertisementRoutes,
  adminAffiliateRoutes,
  adminEmployeeRoutes,
  adminCustomerRoutes,
  adminGunsForSaleRoutes,
  adminMainRoutes,
  adminProductRoutes,
  adminReviewRoutes,
  adminSettingRoutes,
  adminSMSRoutes,
  adminBlogChildRoutes,
  adminSupportRequestRoutes,
} from 'routes/admin/AdminRoutes';

export interface SubMenuItem {
  key: string;
  label: string;
  path: string;
  permission?: string[];
}
export interface TabRedirect {
  path: string;
  permission: string[];
  label: string;
}
export interface MenuGroup {
  title: string;
  items: MenuItem[];
}
export interface MenuItem {
  key: string;
  label: string;
  path?: string;
  children?: SubMenuItem[];
  svgIcon?: React.ReactNode;
  permission?: string[];
  order?: number; // ← persisted order index (set by useSidebarOrder)
  showBadge?: boolean;
  badgeKey?: string;
  activePaths?: string[];
  tabRedirects?: Record<string, any> | TabRedirect[];
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

const STORAGE_KEY = 'adminSidebarOrder';

/** Shape stored: { [groupTitle]: string[] }  e.g. { "Main": ["dashboard","ffl","products","blogs"] } */
export type SidebarOrderMap = Record<string, string[]>;

export const loadSidebarOrderMap = () => {
  try {
    return JSON.parse(getLocalStorage(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

export const saveSidebarOrderMap = (map: SidebarOrderMap): void => {
  storeLocalStorage(STORAGE_KEY, JSON.stringify(map));
};

export const clearSidebarOrderMap = (): void => {
  removeLocalStorage(STORAGE_KEY);
};

// ─── Apply saved order to a menu ─────────────────────────────────────────────

const applyGroupOrder = (items: MenuItem[], savedKeys: string[]): MenuItem[] => {
  if (!savedKeys?.length) return items;
  const ordered = savedKeys
    .map((key) => items.find((i) => i.key === key))
    .filter(Boolean) as MenuItem[];
  // Append any new items that weren't in the saved order
  const extras = items.filter((i) => !savedKeys.includes(i.key));
  return [...ordered, ...extras].map((item, idx) => ({ ...item, order: idx }));
};

export const applyOrderToMenu = (menu: MenuGroup[], saved: SidebarOrderMap): MenuGroup[] =>
  menu.map((group) => ({
    ...group,
    items: applyGroupOrder(group.items, saved[group.title]),
  }));

// ─── Base sidebarMenu definition ──────────────────────────────────────────────
// This is the DEFAULT order. The `order` field is managed at runtime.

export const getSidebarMenu = (): MenuGroup[] => [
  {
    title: t('main'),
    items: [
      {
        key: t('dashboard'),
        label: t('dashboard'),
        svgIcon: <DashboardSvg />,
        path: placeParamsToURL(adminMainRoutes.dashboard.path),
        permission: [permissionSlugs.dashboard],
        // activePaths: [adminMainRoutes.dashboard.path],
        order: 0,
      },
      {
        key: t('ffl'),
        label: t('ffls'),
        svgIcon: <FFLSvg />,
        path: placeParamsToURL(adminMainRoutes.fflList.path),
        permission: [permissionSlugs.ffl_list],
        showBadge: true,
        badgeKey: 'ffl',
        activePaths: [
          adminMainRoutes.fflList.path,
          adminMainRoutes.fflListUI.path,
          adminMainRoutes.fflEditUI.path,
          adminMainRoutes.fflDetail.path,
          adminMainRoutes.fflUpdateDetail.path,
        ],
        order: 1,
      },
      {
        key: t('categories'),
        label: t('categories'),
        svgIcon: <CategoriesSvg />,
        path: placeParamsToURL(adminMainRoutes.categories.path),
        permission: [permissionSlugs.product_category_list],
        activePaths: [adminMainRoutes.categories.path],
        order: 2,
      },
      {
        key: t('products'),
        label: t('products'),
        svgIcon: <ProductSvg />,
        path: placeParamsToURL(adminProductRoutes.product.path),
        permission: [permissionSlugs.product_list, permissionSlugs.product_attribute_list],
        activePaths: [
          adminMainRoutes.productAttributeAdd.path,
          adminMainRoutes.productAttributeEdit.path,
          adminMainRoutes.productAdd.path,
          adminMainRoutes.productEdit.path,
          adminMainRoutes.productDetail.path,
          adminProductRoutes.product.path,
          adminProductRoutes.attributeDynamic.path,
          adminProductRoutes.attributeFixed.path,
        ],
        order: 3,
        tabRedirects: adminProductRoutes,
      },
      {
        key: t('orders'),
        label: t('orders'),
        svgIcon: <ShoppingBagSvg />,
        path: placeParamsToURL(adminMainRoutes.orderList.path),
        permission: [permissionSlugs.order_list],
        activePaths: [adminMainRoutes.orderList.path],
        showBadge: true,
        badgeKey: 'orders',
        order: 4,
      },
      // {
      //   key: t('purchase_forms'),
      //   label: t('purchase_forms'),
      //   svgIcon: <PurchaseFormsSvg />,
      //   path: placeParamsToURL(adminMainRoutes.purchaseFormsList.path),
      //   permission: [permissionSlugs.purchase_forms_list],
      //   activePaths: [adminMainRoutes.purchaseFormsList.path],
      //   order: 5,
      // },
      {
        key: t('blogs'),
        label: t('blog'),
        svgIcon: <BlogSvg />,
        path: placeParamsToURL(adminBlogRoutes.blogList.path),
        permission: [permissionSlugs.blog_list, permissionSlugs.blog_category_list],
        activePaths: [
          adminBlogChildRoutes.blogGridList.path,
          adminMainRoutes.blogAdd.path,
          adminMainRoutes.blogEdit.path,
          adminMainRoutes.blogDetail.path,
          adminBlogRoutes.blogList.path,
          adminBlogRoutes.blogCategoryList.path,
        ],
        order: 6,
      },
      {
        key: t('transfer_request'),
        label: t('transfer_request'),
        svgIcon: <FileTransferSvg />,
        path: placeParamsToURL(adminMainRoutes.transferRequestList.path),
        permission: [permissionSlugs.transfer_list],
        activePaths: [adminMainRoutes.transferRequestList.path],
        showBadge: true,
        badgeKey: 'transfer_requests',
        order: 7,
      },
      {
        key: t('transactions'),
        label: t('transactions'),
        svgIcon: <PaymentCardSvg />,
        path: placeParamsToURL(adminMainRoutes.transactionList.path),
        permission: [permissionSlugs.transaction_list],
        activePaths: [adminMainRoutes.transactionList.path],
        showBadge: true,
        badgeKey: 'transactions',
        order: 8,
      },
      {
        key: t('return_requests'),
        label: t('return_requests'),
        svgIcon: <ReturnBoxSvg />,
        path: placeParamsToURL(adminMainRoutes.returnRequestList.path),
        permission: [permissionSlugs.order_list],
        activePaths: [adminMainRoutes.returnRequestList.path],
        showBadge: true,
        badgeKey: 'return_requests',
        order: 9,
      },
    ],
  },
  {
    title: t('manage'),
    items: [
      {
        key: t('employees'),
        label: t('employees'),
        svgIcon: <EmployeeSvg />,
        path: placeParamsToURL(adminEmployeeRoutes.employeeList.path),
        permission: [permissionSlugs.employee_list],
        activePaths: [adminEmployeeRoutes.employeeList.path, adminEmployeeRoutes.roleList.path],
        order: 0,
      },
      {
        key: t('messages'),
        label: t('send_message'),
        svgIcon: <MessageSvg />,
        path: placeParamsToURL(adminMainRoutes.messageList.path),
        permission: [permissionSlugs.message_list],
        activePaths: [adminMainRoutes.messageList.path, adminMainRoutes.sendMessage.path],
        order: 1,
      },
      {
        key: t('reviews'),
        label: t('reviews'),
        svgIcon: <ReviewSvg />,
        path: placeParamsToURL(adminReviewRoutes.reviewList.path),
        permission: [permissionSlugs.review_list],
        activePaths: [adminReviewRoutes.reviewList.path, adminReviewRoutes.productReviewList.path],
        order: 2,
      },
      {
        key: t('advertisements'),
        label: t('advertisement'),
        svgIcon: <AdvertisementSvg />,
        path: placeParamsToURL(adminAdvertisementRoutes.advertisementList.path),
        permission: [
          permissionSlugs.advertisement_list,
          permissionSlugs.advertisement_location_list,
        ],
        activePaths: [
          adminMainRoutes.advertisementAdd.path,
          adminMainRoutes.advertisementEdit.path,
          adminMainRoutes.advertisementView.path,
          adminAdvertisementRoutes.advertisementList.path,
          adminAdvertisementRoutes.advertisementLocation.path,
        ],
        order: 3,
        // children: [
        //   {
        //     key: t('all_advertisements'),
        //     label: t('all_advertisements'),
        //     path: placeParamsToURL(adminAdvertisementRoutes.advertisementList.path),
        //     permission: [permissionSlugs.advertisement_list],
        //   },
        //   {
        //     key: t('location'),
        //     label: t('location'),
        //     path: placeParamsToURL(adminAdvertisementRoutes.advertisementLocation.path),
        //     permission: [permissionSlugs.advertisement_location_list],
        //   },
        // ],
      },
      {
        key: t('guns_for_sale'),
        label: t('guns_for_sale'),
        svgIcon: <GunSvg width="22" height="22" />,
        path: placeParamsToURL(adminGunsForSaleRoutes.activeGunList.path),
        permission: [permissionSlugs.active_gun_list],
        activePaths: [
          adminGunsForSaleRoutes.activeGunList.path,
          adminGunsForSaleRoutes.soldGunList.path,
        ],
        order: 4,
      },
      {
        key: t('affiliate_code'),
        label: t('affiliate_code'),
        svgIcon: <AffiliateSvg />,
        path: placeParamsToURL(adminAffiliateRoutes.analysis.path),
        permission: [permissionSlugs.affiliate_code_list],
        activePaths: [adminAffiliateRoutes.analysis.path, adminAffiliateRoutes.codeList.path],
        order: 5,
      },
      {
        key: t('subscription'),
        label: t('subscription'),
        svgIcon: <SubscriptionSvg />,
        path: placeParamsToURL(adminMainRoutes.subscriptionPlanList.path),
        permission: [permissionSlugs.subscription_plan_list],
        activePaths: [
          adminMainRoutes.subscriptionPlanList.path,
          adminMainRoutes.subscriptionPlanEdit.path,
          adminMainRoutes.subscriptionPlanAdd.path,
        ],
        order: 6,
      },

      // {
      //   key: t('product_attributes'),
      //   label: t('product_attributes'),
      //   svgIcon: <CategoriesSvg />,
      //   path: placeParamsToURL(adminMainRoutes.productAttributeList.path),
      //   permission: [permissionSlugs.product_attribute_list],
      // },
      // {
      //   key: t('settings'),
      //   label: t('settings'),
      //   svgIcon: <DashboardSvg />,
      //   path: placeParamsToURL(adminMainRoutes.settings.path),
      // },

      {
        key: t('content_pages'),
        label: t('content_pages'),
        svgIcon: <ContentPagesSvg />,
        path: placeParamsToURL(adminMainRoutes.contentPagesList.path),
        permission: [permissionSlugs.content_page_list],
        activePaths: [
          adminMainRoutes.contentPagesList.path,
          adminMainRoutes.contentPageAdd.path,
          adminMainRoutes.contentPageEdit.path,
          adminMainRoutes.contentPageDetail.path,
        ],
        order: 7,
      },

      {
        key: t('manufacturer'),
        label: t('manufacturers'),
        svgIcon: <MenufacturereSvg />,
        path: placeParamsToURL(adminMainRoutes.manufacturers.path),
        permission: [permissionSlugs.manufacturer_list],
        activePaths: [adminMainRoutes.manufacturers.path],
        order: 8,
      },
      {
        key: t('faq'),
        label: t('faq'),
        svgIcon: <FAQSvg />,
        path: placeParamsToURL(adminMainRoutes.faqs.path),
        permission: [permissionSlugs.faq_list],
        activePaths: [adminMainRoutes.faqs.path],
        order: 9,
      },
      {
        key: t('other'),
        label: t('other'),
        svgIcon: <OtherSvg />,
        path: placeParamsToURL(adminMainRoutes.dynamicModules.path, {
          moduleName: 'payment-gateway',
        }),
        permission: [permissionSlugs.faq_list],
        activePaths: [adminMainRoutes.dynamicModules.path.split('/:')[0]],
        order: 10,
      },
    ],
  },
  {
    title: t('store_pages'),
    items: [
      {
        key: t('customers'),
        label: t('customers'),
        svgIcon: <CustomerSvg />,
        path: placeParamsToURL(adminCustomerRoutes.customerList.path),
        permission: [permissionSlugs.customer_list, permissionSlugs.customer_group_list],
        activePaths: [
          adminCustomerRoutes.customerList.path,
          adminCustomerRoutes.customerGroupList.path,
          adminMainRoutes.editCustomer.path,
        ],
        order: 0,
      },
      {
        key: t('email_templates'),
        label: t('email'),
        svgIcon: <OpenEmailSvg />,
        path: placeParamsToURL(adminMainRoutes.emailTemplateList.path),
        permission: [permissionSlugs.email_template_list],
        activePaths: [
          adminMainRoutes.emailTemplateList.path,
          adminMainRoutes.editEmailTemplate.path,
        ],
        order: 1,
      },
      {
        key: t('sms'),
        label: t('sms'),
        svgIcon: <SMSTemplateSvg />,
        path: placeParamsToURL(adminSMSRoutes.smsLogList.path),
        permission: [permissionSlugs.sms_log_list, permissionSlugs.email_template_list],
        activePaths: [
          adminSMSRoutes.smsLogList.path,
          adminSMSRoutes.smsTemplateList.path,
          adminMainRoutes.editSMSTemplate.path,
        ],
        order: 2,
      },
      {
        key: t('coupon_codes'),
        label: t('coupon_codes'),
        svgIcon: <CouponSvg />,
        path: placeParamsToURL(adminMainRoutes.couponList.path),
        permission: [permissionSlugs.coupon_list],
        activePaths: [
          adminMainRoutes.couponList.path,
          adminMainRoutes.couponAdd.path,
          adminMainRoutes.couponEdit.path,
        ],
        order: 3,
      },
      {
        key: t('url_rewrite'),
        label: t('url_rewrite'),
        svgIcon: <URLRewriteSvg />,
        path: placeParamsToURL(adminMainRoutes.urlRewriteList.path),
        permission: [permissionSlugs.url_rewrite_list],
        activePaths: [adminMainRoutes.urlRewriteList.path],
        order: 4,
      },
      {
        key: t('support_requests'),
        label: t('support_requests'),
        svgIcon: <ReviewSvg />,
        path: placeParamsToURL(adminSupportRequestRoutes.orderReportList.path),
        permission: [permissionSlugs.order_report_list, permissionSlugs.integration_request_list],
        activePaths: [
          adminSupportRequestRoutes.orderReportList.path,
          adminSupportRequestRoutes.integrationRequestList.path,
          adminSupportRequestRoutes.productHelpRequestList.path,
          adminSupportRequestRoutes.fieldRequestList.path,
        ],
        order: 5,
        tabRedirects: adminSupportRequestRoutes,
      },
      {
        key: t('search_terms'),
        label: t('search_terms'),
        svgIcon: <SearchTermsSvg />,
        path: placeParamsToURL(adminMainRoutes.searchTermsList.path),
        permission: [permissionSlugs.search_terms_list],
        activePaths: [adminMainRoutes.searchTermsList.path],
        order: 6,
      },
      {
        key: t('settings'),
        label: t('general_settings'),
        svgIcon: <SettingSvg />,
        path: placeParamsToURL(adminSettingRoutes.generalSetting.path),
        permission: [permissionSlugs.general_setting],
        activePaths: [adminSettingRoutes.generalSetting.path],
        order: 7,
      },
      {
        key: t('banners'),
        label: t('banners'),
        svgIcon: <GallerySvg width={20} height={20} />,
        path: placeParamsToURL(adminMainRoutes.banners.path),
        permission: [permissionSlugs.banner_list],
        activePaths: [adminMainRoutes.banners.path],
        order: 8,
      },
    ],
  },
];
